/**
 * ### New version {{#crossLink "Services.services.copyrightsCaptionV2"}}copyrightsCaptionV2{{/crossLink}}
 * The new {{#crossLink "Services.services.copyrightsCaptionV2"}}copyrightsCaptionV2{{/crossLink}}
 * service was introduced.
 * We highly recommend using the new V2 version.
 *
 * ### Old version
 * * __The copyrightsCaption service has been deprecated.__
 * * This service will be withdrawn following a 12 months deprecation period.
 * * The planned withdrawal period is June of 2022.
 * * Following withdrawal, requests to this service may receive an HTTP 404 error in response.
 *
 * The copyright caption service implementation provides a caption that is supposed to be displayed on a link
 * that provides the full copyright notice. The response is not changed often.
 *
 * This service is supposed to be called once when user has the map displayed for the first time.
 *
 * ### Response
 * The response from the service is an object containing "copyrightsCaption", e.g.:
 * ```
 * {
 *   "formatVersion": "0.0.1",
 *   "copyrightsCaption": "© 1992 - 2021 TomTom."
 * }
 *```
 *
 * The response is also extended with `getTrackingId()` method, which returns the `Tracking-ID`
 * associated with the request.
 *
 * This text ("© 1992 - 2021 TomTom.") has to be displayed as a copyrights prompt when using TomTom services.
 * The caption should be clickable. User should be presented with the full copyrights notice when
 * the caption is clicked. Full copyright notice can be retrieved from the
 * {{#crossLink "Services.services.copyrights"}}Copyrights service{{/crossLink}}.
 *
 * @example
 * ```js
 * tt.services.copyrightsCaption({
 *   key: <Your API key>
*  }).then(function(response) {
 *   console.log(response);
 * });
 * ```
 *
 * @class copyrightsCaption
 * @namespace Services.services
 * @module Services
 * @uses TrackingIdMixin
 * @constructor
 * @uses KeyMixin
 * @uses ProtocolMixin
 * @uses AbortSignalMixin
 *
 * @param {Object} [options]
 * @param {Object} [additionalOptions] Additional options to be passed to the service.
 */
import {SERVICE_TYPES} from 'Core/serviceTypes';
import parameterApplications from '../../common/parameterApplications';
import { modelResponse } from '../../model/modelResponse';
import { v4 as uuid } from 'uuid';
import {
    string as validateString, key as validateKey, trackingId as validateTrackingId,
    oneOfValue as validateOneOfValue
} from '../validators';
import { Endpoints } from '../../endpoints/endpointsManager';
import { singleRequestServiceFactory, serviceFactory } from '../../core';

const fields = {
    key: {
        validators: [validateKey]
    },
    trackingId: {
        validators: [validateTrackingId],
        application: parameterApplications.HEADER,
        defaultValue: uuid
    },
    protocol: {
        validators: [validateOneOfValue(['http', 'https'], 'protocol')]
    },
    sessionId: {
        validators: [validateString]
    }
};

export function copyrightsCaption(options, additionalOptions) {
    const endpoints = new Endpoints(additionalOptions);
    const singleRequest = singleRequestServiceFactory(endpoints.resolve('captionEndpoint'));
    function handleServiceCall(data, abortSignal) {
        return singleRequest(fields, data, abortSignal).then(modelResponse);
    }
    return serviceFactory(
        fields,
        SERVICE_TYPES.MAP,
        'copyrightsCaption',
        handleServiceCall
    )(options, additionalOptions);
}
